import { useMemo, useState, type KeyboardEvent } from "react";
import { quadraticCost } from "@voting/quadratic";
import { BudgetRing } from "./BudgetRing";

type Opt = { id: number; content: string };

function costForDelta(current: number, delta: number): number {
	const next = current + delta;
	return next * next - current * current;
}

type QuadraticBallotProps = {
	options: Opt[];
	alloc: Record<number, number>;
	onAllocChange: (next: Record<number, number>) => void;
	disabled: boolean;
};

export function QuadraticBallot({ options, alloc, onAllocChange, disabled }: QuadraticBallotProps) {
	const [shake, setShake] = useState(false);

	const allocStrings = useMemo(() => {
		const r: Record<string, number> = {};
		for (const [k, v] of Object.entries(alloc)) {
			r[String(k)] = v;
		}
		return r;
	}, [alloc]);

	const spent = useMemo(() => quadraticCost(allocStrings), [allocStrings]);
	const remaining = 100 - spent;

	const applyValue = (id: number, next: number) => {
		if (disabled || next < 0) return;
		const trial = { ...alloc, [id]: next };
		const trialStr: Record<string, number> = {};
		for (const [k, v] of Object.entries(trial)) trialStr[String(k)] = v;
		const c = quadraticCost(trialStr);
		if (c > 100) {
			setShake(true);
			setTimeout(() => setShake(false), 400);
			return;
		}
		onAllocChange(trial);
	};

	const bump = (id: number, delta: number) => {
		const cur = alloc[id] ?? 0;
		applyValue(id, cur + delta);
	};

	return (
		<div id="wi-quadratic-ballot" className="space-y-6">
			<div className="flex flex-col items-center gap-2">
				<BudgetRing remaining={remaining} shake={shake} />
			</div>
			<p className="sr-only">
				Quadratic ballot: on small screens use the point slider per option; on all sizes use minus and plus keys or the
				large buttons to adjust points before casting.
			</p>
			<ul className="space-y-3">
				{options.map((o) => {
					const v = alloc[o.id] ?? 0;
					const minusCost = v > 0 ? costForDelta(v, -1) : 0;
					const plusCost = costForDelta(v, 1);
					return (
						<li
							key={o.id}
							tabIndex={disabled ? -1 : 0}
							className="rounded-xl border border-wi-text/10 bg-wi-surface p-4 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-wi-gold/35 focus-visible:ring-offset-2 focus-visible:ring-offset-wi-bg"
							onKeyDown={(e: KeyboardEvent<HTMLLIElement>) => {
								if (disabled) return;
								if (e.key === "-" || e.key === "_") {
									e.preventDefault();
									bump(o.id, -1);
								}
								if (e.key === "+" || e.key === "=") {
									e.preventDefault();
									bump(o.id, 1);
								}
							}}
						>
							<div className="flex flex-wrap items-center justify-between gap-3">
								<p className="min-w-0 flex-1 font-medium text-sm text-wi-text">{o.content}</p>
								<div className="flex items-center gap-2">
									<button
										type="button"
										disabled={disabled || v <= 0}
										className="inline-flex size-12 shrink-0 touch-manipulation items-center justify-center rounded-lg border border-wi-text/20 text-lg leading-none hover:border-wi-gold/40 disabled:opacity-30"
										onClick={() => bump(o.id, -1)}
									>
										−
									</button>
									<span className="w-8 text-center font-mono text-wi-text text-sm">{v}</span>
									<button
										type="button"
										disabled={disabled}
										className="inline-flex size-12 shrink-0 touch-manipulation items-center justify-center rounded-lg border border-wi-text/20 text-lg leading-none hover:border-wi-gold/40 disabled:opacity-30"
										onClick={() => bump(o.id, 1)}
									>
										+
									</button>
								</div>
							</div>
							<div className="mt-3 space-y-2 md:hidden">
								<label className="flex items-center justify-between gap-3 text-wi-text/55 text-xs">
									<span>Points (mobile slider)</span>
									<span className="font-mono text-wi-text">{v}</span>
								</label>
								<input
									type="range"
									min={0}
									max={10}
									step={1}
									value={v}
									disabled={disabled}
									className="h-12 w-full touch-manipulation accent-wi-gold"
									aria-label={`Points for ${o.content}`}
									onChange={(e) => applyValue(o.id, Number(e.target.value))}
								/>
							</div>
							<p className="mt-2 text-wi-text/55 text-xs">
								v² for next +1: −{plusCost} pts
								{v > 0 ? ` · v² for −1: +${minusCost} pts` : null}
							</p>
						</li>
					);
				})}
			</ul>
		</div>
	);
}

export function emptyQuadraticAlloc(options: Opt[]): Record<number, number> {
	const o: Record<number, number> = {};
	for (const x of options) o[x.id] = 0;
	return o;
}

export function quadraticPayloadFromAlloc(alloc: Record<number, number>): { kind: "quadratic"; allocations: Record<string, number> } {
	const out: Record<string, number> = {};
	for (const [k, v] of Object.entries(alloc)) {
		if (typeof v === "number" && Number.isInteger(v) && v >= 0) {
			out[String(k)] = v;
		}
	}
	return { kind: "quadratic", allocations: out };
}
